/**
 * Chart Context Registry
 *
 * Central allowlist of valid context groups and chart names.
 * All chart context identifiers MUST be declared here to prevent
 * duplicate or arbitrary strings scattered across components.
 *
 * When adding a new chart to the Advisor integration:
 * 1. Add a new entry to CHART_CONTEXT_GROUPS (if the section is new)
 * 2. Add chart name entries to CHART_NAMES
 * 3. Use the constants in the component that creates the chartContextId prop
 *    and in the layout that sets contextGroup on BeaconChartWithLegend
 *
 * @see ARCHITECTURE.md for full design documentation.
 */

// ---------------------------------------------------------------------------
// Context Groups (sections)
// ---------------------------------------------------------------------------

/**
 * Valid context group identifiers.
 * Each group corresponds to a visual section that wraps one or more charts
 * inside a BeaconChartWithLegend with a shared "Ask Advisor" action.
 */
export const CHART_CONTEXT_GROUPS = {
  /** Traffic section: donut + comparison/current bar charts */
  TRAFFIC: 'traffic',
  /** Total Traffic section: spline chart showing total traffic over time */
  TOTAL_TRAFFIC: 'total-traffic',
  /** Share of Total Traffic section: spline chart showing market share % */
  SHARE_OF_TRAFFIC: 'share-of-traffic',
  /** Traffic by Keyword section: column chart grouped by keyword */
  TRAFFIC_BY_KEYWORD: 'traffic-by-keyword',
  /** Branded/Unbranded Traffic section: spline chart for branded vs unbranded share */
  BRANDED_UNBRANDED_TRAFFIC: 'branded-unbranded-traffic',
  /** Top Entity Traffic section: column chart with double dropdown (entity + metric) */
  TRAFFIC_TOP_ENTITY: 'traffic-top-entity',
  /** Brand → Category/Subcategory top entity chart with double dropdown */
  TRAFFIC_TOP_ENTITY_BRAND: 'traffic-top-entity-brand',
  /** Filterable Stacked Bar section (keyword): stacked/grouped column chart with brand filtering */
  FILTERABLE_STACKED_BAR: 'filterable-stacked-bar',
  /** Filterable Stacked Bar section (category/subcategory): stacked/grouped column chart */
  FILTERABLE_STACKED_BAR_CATEGORY: 'filterable-stacked-bar-category',
  /** Keyword Count with Total Traffic section: spline chart showing keyword count over time */
  KEYWORD_COUNT_TRAFFIC: 'keyword-count-traffic',

  // ── Organic Traffic V2 sections ──

  /** Organic Traffic spline chart */
  ORGANIC_V2_TRAFFIC: 'organic-v2-traffic',
  /** Share of Organic Traffic spline chart */
  ORGANIC_V2_SHARE: 'organic-v2-share',
  /** Keyword single dropdown top entity chart */
  ORGANIC_V2_TOP_ENTITY_KEYWORD: 'organic-v2-top-entity-keyword',
  /** Double dropdown top entity chart (category/brand) */
  ORGANIC_V2_TOP_ENTITY: 'organic-v2-top-entity',
  /** Filterable stacked bar chart (keyword) */
  ORGANIC_V2_FILTERABLE_STACKED_BAR_KEYWORD: 'organic-v2-filterable-stacked-bar-keyword',
  /** Keyword Count with Organic Traffic spline chart */
  ORGANIC_V2_KEYWORD_COUNT: 'organic-v2-keyword-count',
  /** Double dropdown top entity chart (company-level) */
  ORGANIC_V2_TOP_ENTITY_COMPANY: 'organic-v2-top-entity-company',
  /** Double dropdown top entity chart (brand → category/subcategory) */
  ORGANIC_V2_TOP_ENTITY_BRAND: 'organic-v2-top-entity-brand',
  /** Branded/Unbranded organic traffic spline chart */
  ORGANIC_V2_BRANDED_UNBRANDED: 'organic-v2-branded-unbranded',
  /** Filterable stacked bar chart (category/subcategory) */
  ORGANIC_V2_FILTERABLE_STACKED_BAR_CATEGORY: 'organic-v2-filterable-stacked-bar-category',

  // ── Paid Traffic sections ──

  /** Paid Search Traffic spline chart */
  PAID_TRAFFIC: 'paid-traffic',
  /** Paid Search Spend spline chart */
  PAID_TRAFFIC_AD_SPEND: 'paid-traffic-ad-spend',
  /** Share of Paid Search Traffic spline chart */
  PAID_TRAFFIC_SHARE: 'paid-traffic-share',
  /** Top Entity Paid Traffic column chart with double dropdown */
  PAID_TRAFFIC_TOP_ENTITY: 'paid-traffic-top-entity',
  /** Products with Paid Search Traffic spline chart */
  PAID_TRAFFIC_PRODUCTS: 'paid-traffic-products',
  /** Keyword Count with Paid Search Traffic spline chart */
  PAID_TRAFFIC_KEYWORD_COUNT: 'paid-traffic-keyword-count',
  /** Branded/Unbranded Paid Traffic spline chart */
  PAID_TRAFFIC_BRANDED_UNBRANDED: 'paid-traffic-branded-unbranded',
  /** Filterable Stacked Bar for paid traffic by keyword */
  PAID_TRAFFIC_FILTERABLE_STACKED_BAR: 'paid-traffic-filterable-stacked-bar',
  /** Brand → Category/Subcategory top entity chart for paid traffic */
  PAID_TRAFFIC_TOP_ENTITY_BRAND: 'paid-traffic-top-entity-brand',
  /** Filterable Stacked Bar for paid traffic by category/subcategory */
  PAID_TRAFFIC_FILTERABLE_STACKED_BAR_CATEGORY: 'paid-traffic-filterable-stacked-bar-category',

  // ── Other Traffic sections ──

  /** Other Traffic spline chart */
  OTHER_TRAFFIC: 'other-traffic',
  /** Products with Other Traffic spline chart */
  OTHER_TRAFFIC_PRODUCTS: 'other-traffic-products',

  // ── Sales Summary sections ──

  /** Retail Sales spline chart (1P/3P selector in 1P branch, plain in non-1P branch) */
  RETAIL_SALES: 'retail-sales',
  /** Market Share of Retail Sales spline chart (multiple field options) */
  MARKET_SHARE_RETAIL_SALES: 'market-share-retail-sales',
  /** Product Count spline chart */
  SALES_PRODUCT_COUNT: 'sales-product-count',
  /** 1P Retail Sales as % of Total spline chart */
  SALES_1P_PERCENT: 'sales-1p-percent',
  /** Market Share column chart (both 1P and non-1P variants) */
  SALES_MARKET_SHARE_COLUMN: 'sales-market-share-column',
  /** Top entity double dropdown chart (company-level, 1P branch) */
  SALES_TOP_ENTITY: 'sales-top-entity',
  /** Top entity double dropdown chart (category/brand/etc. and non-1P) */
  SALES_TOP_ENTITY_BROAD: 'sales-top-entity-broad',
  /** Product market share spline chart (product entity only) */
  SALES_MARKET_SHARE_PRODUCT: 'sales-market-share-product',

  // ── Conversion sections ──

  /** Conversion rate spline chart */
  CONVERSION_SPLINE: 'conversion-spline',
  /** Conversion top entity column chart */
  CONVERSION_TOP_ENTITY: 'conversion-top-entity',

  // ── Price Summary sections ──

  /** Retail Price spline chart */
  PRICE_RETAIL_PRICE: 'price-retail-price',
  /** Price Index spline chart */
  PRICE_INDEX: 'price-index',
  /** Price top entity column chart */
  PRICE_TOP_ENTITY: 'price-top-entity',

  // ── Price Promotion sections ──

  /** Promotions top section spline chart */
  PROMOTION_TOP_SECTION: 'promotion-top-section',
  /** Promotions top entity column chart (entity dropdown) */
  PROMOTION_TOP_ENTITY: 'promotion-top-entity',
  /** Promotions deal type column chart (deal type dropdown) */
  PROMOTION_DEAL_TYPE: 'promotion-deal-type',

  // ── Units Sold sections ──

  /** Units Sold spline chart (1P/3P or plain) */
  UNITS_SOLD_SPLINE: 'units-sold-spline',
  /** Market Share of Units Sold spline chart */
  UNITS_SOLD_MARKET_SHARE: 'units-sold-market-share',
  /** Product Count spline chart (units sold context) */
  UNITS_SOLD_PRODUCT_COUNT: 'units-sold-product-count',
  /** 1P Total Units Sold spline chart (company-level) */
  UNITS_SOLD_1P_TOTAL: 'units-sold-1p-total',
  /** Market Share column chart for units sold */
  UNITS_SOLD_MARKET_SHARE_COLUMN: 'units-sold-market-share-column',
  /** Top entity double dropdown chart for units sold (company) */
  UNITS_SOLD_TOP_ENTITY: 'units-sold-top-entity',
  /** Top entity double dropdown chart for units sold (broad) */
  UNITS_SOLD_TOP_ENTITY_BROAD: 'units-sold-top-entity-broad',
  /** 1P Total Units Sold spline chart (broad entities) */
  UNITS_SOLD_1P_TOTAL_BROAD: 'units-sold-1p-total-broad',
  /** Product market share spline chart for units sold */
  UNITS_SOLD_MARKET_SHARE_PRODUCT: 'units-sold-market-share-product',
  /** Non-1P market share spline chart for units sold */
  UNITS_SOLD_NON1P_MARKET_SHARE: 'units-sold-non1p-market-share',
  /** Non-1P market share column chart for units sold */
  UNITS_SOLD_NON1P_MARKET_SHARE_COLUMN: 'units-sold-non1p-market-share-column',
  /** Non-1P top entity double dropdown chart for units sold */
  UNITS_SOLD_NON1P_TOP_ENTITY: 'units-sold-non1p-top-entity'
} as const;

export type ChartContextGroup = (typeof CHART_CONTEXT_GROUPS)[keyof typeof CHART_CONTEXT_GROUPS];

// ---------------------------------------------------------------------------
// Context Group Descriptions
// ---------------------------------------------------------------------------

/**
 * Default description for each context group, sent with the Ask Advisor payload.
 * Resolved automatically by BeaconChartWithLegend from the active contextGroup.
 */
export const CHART_CONTEXT_DESCRIPTIONS: Record<ChartContextGroup, string> = {
  [CHART_CONTEXT_GROUPS.TRAFFIC]: 'Donut and bar charts for total traffic',
  [CHART_CONTEXT_GROUPS.TOTAL_TRAFFIC]: 'Spline chart for total traffic over time',
  [CHART_CONTEXT_GROUPS.SHARE_OF_TRAFFIC]: 'Spline chart for share of total traffic over time',
  [CHART_CONTEXT_GROUPS.TRAFFIC_BY_KEYWORD]: 'Column chart for traffic grouped by keyword',
  [CHART_CONTEXT_GROUPS.BRANDED_UNBRANDED_TRAFFIC]: 'Spline chart for branded vs unbranded traffic share',
  [CHART_CONTEXT_GROUPS.TRAFFIC_TOP_ENTITY]: 'Column chart for top entity traffic with double dropdown',
  [CHART_CONTEXT_GROUPS.TRAFFIC_TOP_ENTITY_BRAND]: 'Column chart for brand traffic by category/subcategory',
  [CHART_CONTEXT_GROUPS.FILTERABLE_STACKED_BAR]: 'Filterable stacked bar chart for traffic by keyword',
  [CHART_CONTEXT_GROUPS.FILTERABLE_STACKED_BAR_CATEGORY]: 'Filterable stacked bar chart for traffic by category/subcategory',
  [CHART_CONTEXT_GROUPS.KEYWORD_COUNT_TRAFFIC]: 'Spline chart for keyword count with total traffic over time',

  // ── Organic Traffic V2 ──
  [CHART_CONTEXT_GROUPS.ORGANIC_V2_TRAFFIC]: 'Spline chart for organic traffic over time',
  [CHART_CONTEXT_GROUPS.ORGANIC_V2_SHARE]: 'Spline chart for share of organic traffic over time',
  [CHART_CONTEXT_GROUPS.ORGANIC_V2_TOP_ENTITY_KEYWORD]: 'Column chart for organic traffic by keyword (single dropdown)',
  [CHART_CONTEXT_GROUPS.ORGANIC_V2_TOP_ENTITY]: 'Column chart for organic traffic top entity with double dropdown',
  [CHART_CONTEXT_GROUPS.ORGANIC_V2_FILTERABLE_STACKED_BAR_KEYWORD]: 'Filterable stacked bar chart for organic traffic by keyword',
  [CHART_CONTEXT_GROUPS.ORGANIC_V2_KEYWORD_COUNT]: 'Spline chart for keyword count with organic traffic over time',
  [CHART_CONTEXT_GROUPS.ORGANIC_V2_TOP_ENTITY_COMPANY]: 'Column chart for organic traffic top entity (company-level)',
  [CHART_CONTEXT_GROUPS.ORGANIC_V2_TOP_ENTITY_BRAND]: 'Column chart for organic traffic by brand category/subcategory',
  [CHART_CONTEXT_GROUPS.ORGANIC_V2_BRANDED_UNBRANDED]: 'Spline chart for branded vs unbranded organic traffic share',
  [CHART_CONTEXT_GROUPS.ORGANIC_V2_FILTERABLE_STACKED_BAR_CATEGORY]: 'Filterable stacked bar chart for organic traffic by category/subcategory',

  // ── Paid Traffic ──
  [CHART_CONTEXT_GROUPS.PAID_TRAFFIC]: 'Spline chart for paid search traffic over time',
  [CHART_CONTEXT_GROUPS.PAID_TRAFFIC_AD_SPEND]: 'Spline chart for paid search spend over time',
  [CHART_CONTEXT_GROUPS.PAID_TRAFFIC_SHARE]: 'Spline chart for share of paid search traffic over time',
  [CHART_CONTEXT_GROUPS.PAID_TRAFFIC_TOP_ENTITY]: 'Column chart for top entity paid traffic with double dropdown',
  [CHART_CONTEXT_GROUPS.PAID_TRAFFIC_PRODUCTS]: 'Spline chart for products with paid search traffic over time',
  [CHART_CONTEXT_GROUPS.PAID_TRAFFIC_KEYWORD_COUNT]: 'Spline chart for keyword count with paid search traffic over time',
  [CHART_CONTEXT_GROUPS.PAID_TRAFFIC_BRANDED_UNBRANDED]: 'Spline chart for branded vs unbranded paid traffic share',
  [CHART_CONTEXT_GROUPS.PAID_TRAFFIC_FILTERABLE_STACKED_BAR]: 'Filterable stacked bar chart for paid traffic by keyword',
  [CHART_CONTEXT_GROUPS.PAID_TRAFFIC_TOP_ENTITY_BRAND]: 'Column chart for brand paid traffic by category/subcategory',
  [CHART_CONTEXT_GROUPS.PAID_TRAFFIC_FILTERABLE_STACKED_BAR_CATEGORY]: 'Filterable stacked bar chart for paid traffic by category/subcategory',

  // ── Other Traffic ──
  [CHART_CONTEXT_GROUPS.OTHER_TRAFFIC]: 'Spline chart for other traffic over time',
  [CHART_CONTEXT_GROUPS.OTHER_TRAFFIC_PRODUCTS]: 'Spline chart for products with other traffic over time',

  // ── Sales Summary ──
  [CHART_CONTEXT_GROUPS.RETAIL_SALES]: 'Spline chart for retail sales over time',
  [CHART_CONTEXT_GROUPS.MARKET_SHARE_RETAIL_SALES]: 'Spline chart for market share of retail sales over time',
  [CHART_CONTEXT_GROUPS.SALES_PRODUCT_COUNT]: 'Spline chart for product count over time',
  [CHART_CONTEXT_GROUPS.SALES_1P_PERCENT]: 'Spline chart for 1P retail sales as percentage of total',
  [CHART_CONTEXT_GROUPS.SALES_MARKET_SHARE_COLUMN]: 'Column chart for market share of retail sales by entity',
  [CHART_CONTEXT_GROUPS.SALES_TOP_ENTITY]: 'Column chart for top entity retail sales with double dropdown',
  [CHART_CONTEXT_GROUPS.SALES_TOP_ENTITY_BROAD]: 'Column chart for top entity retail sales by category/brand',
  [CHART_CONTEXT_GROUPS.SALES_MARKET_SHARE_PRODUCT]: 'Spline chart for product market share of retail sales',

  // ── Conversion ──
  [CHART_CONTEXT_GROUPS.CONVERSION_SPLINE]: 'Spline chart for conversion rate over time',
  [CHART_CONTEXT_GROUPS.CONVERSION_TOP_ENTITY]: 'Column chart for conversion rate by top entity',

  // ── Price Summary ──
  [CHART_CONTEXT_GROUPS.PRICE_RETAIL_PRICE]: 'Spline chart for retail price over time',
  [CHART_CONTEXT_GROUPS.PRICE_INDEX]: 'Spline chart for price index over time',
  [CHART_CONTEXT_GROUPS.PRICE_TOP_ENTITY]: 'Column chart for price by top entity with double dropdown',

  // ── Price Promotion ──
  [CHART_CONTEXT_GROUPS.PROMOTION_TOP_SECTION]: 'Spline chart for promotions overview over time',
  [CHART_CONTEXT_GROUPS.PROMOTION_TOP_ENTITY]: 'Column chart for promotions by top entity',
  [CHART_CONTEXT_GROUPS.PROMOTION_DEAL_TYPE]: 'Column chart for promotions by deal type',

  // ── Units Sold ──
  [CHART_CONTEXT_GROUPS.UNITS_SOLD_SPLINE]: 'Spline chart for units sold over time',
  [CHART_CONTEXT_GROUPS.UNITS_SOLD_MARKET_SHARE]: 'Spline chart for market share of units sold over time',
  [CHART_CONTEXT_GROUPS.UNITS_SOLD_PRODUCT_COUNT]: 'Spline chart for product count (units sold context)',
  [CHART_CONTEXT_GROUPS.UNITS_SOLD_1P_TOTAL]: 'Spline chart for 1P total units sold (company-level)',
  [CHART_CONTEXT_GROUPS.UNITS_SOLD_MARKET_SHARE_COLUMN]: 'Column chart for market share of units sold by entity',
  [CHART_CONTEXT_GROUPS.UNITS_SOLD_TOP_ENTITY]: 'Column chart for top entity units sold with double dropdown (company)',
  [CHART_CONTEXT_GROUPS.UNITS_SOLD_TOP_ENTITY_BROAD]: 'Column chart for top entity units sold with double dropdown (broad)',
  [CHART_CONTEXT_GROUPS.UNITS_SOLD_1P_TOTAL_BROAD]: 'Spline chart for 1P total units sold (broad entities)',
  [CHART_CONTEXT_GROUPS.UNITS_SOLD_MARKET_SHARE_PRODUCT]: 'Spline chart for product market share of units sold',
  [CHART_CONTEXT_GROUPS.UNITS_SOLD_NON1P_MARKET_SHARE]: 'Spline chart for non-1P market share of units sold',
  [CHART_CONTEXT_GROUPS.UNITS_SOLD_NON1P_MARKET_SHARE_COLUMN]: 'Column chart for non-1P market share of units sold by entity',
  [CHART_CONTEXT_GROUPS.UNITS_SOLD_NON1P_TOP_ENTITY]: 'Column chart for non-1P top entity units sold with double dropdown',
};

// ---------------------------------------------------------------------------
// Chart Names
// ---------------------------------------------------------------------------

/**
 * Valid chart name identifiers.
 * Each name uniquely identifies a chart within the app.
 * These are combined with the app prefix, context group, and URL suffix
 * to form the full chart ID stored in the Zustand store / DynamoDB.
 *
 * Full ID format: {app}#{contextGroup}#{chartName}#{urlSuffix}
 * Example: atlas#traffic#traffic-donut#category/1280?rid=1&tab=trafficV2&subtab=totalClicks
 */
export const CHART_NAMES = {
  /** Donut chart showing total traffic breakdown (organic, paid, other) */
  TRAFFIC_DONUT: 'traffic-donut',
  /** Bar chart for the comparison time period */
  TRAFFIC_BAR_COMPARISON: 'traffic-bar-comparison',
  /** Bar chart for the current time period */
  TRAFFIC_BAR_CURRENT: 'traffic-bar-current',
  /** Spline chart showing total traffic over time */
  TOTAL_TRAFFIC_SPLINE: 'total-traffic-spline',
  /** Spline chart showing share of total traffic over time */
  SHARE_OF_TRAFFIC_SPLINE: 'share-of-traffic-spline',
  /** Column chart showing traffic grouped by keyword */
  TRAFFIC_BY_KEYWORD_COLUMN: 'traffic-by-keyword-column',
  /** Spline chart showing branded vs unbranded traffic share */
  BRANDED_UNBRANDED_SPLINE: 'branded-unbranded-spline',
  /** Column chart showing top entity traffic with double dropdown */
  TRAFFIC_TOP_ENTITY_COLUMN: 'traffic-top-entity-column',
  /** Stacked/grouped column chart with brand filtering */
  FILTERABLE_STACKED_BAR_CHART: 'filterable-stacked-bar-chart',
  /** Spline chart showing keyword count with total traffic over time */
  KEYWORD_COUNT_TRAFFIC_SPLINE: 'keyword-count-traffic-spline',

  // ── Organic Traffic V2 charts ──

  /** Spline chart for organic traffic over time */
  ORGANIC_V2_TRAFFIC_SPLINE: 'organic-v2-traffic-spline',
  /** Spline chart for share of organic traffic over time */
  ORGANIC_V2_SHARE_SPLINE: 'organic-v2-share-spline',
  /** Column chart for organic traffic top entity */
  ORGANIC_V2_TOP_ENTITY_COLUMN: 'organic-v2-top-entity-column',
  /** Spline chart for keyword count with organic traffic over time */
  ORGANIC_V2_KEYWORD_COUNT_SPLINE: 'organic-v2-keyword-count-spline',

  // ── Paid Traffic charts ──

  /** Spline chart for paid search traffic over time */
  PAID_TRAFFIC_SPLINE: 'paid-traffic-spline',
  /** Spline chart for paid search spend over time */
  PAID_TRAFFIC_AD_SPEND_SPLINE: 'paid-traffic-ad-spend-spline',
  /** Spline chart for share of paid search traffic over time */
  PAID_TRAFFIC_SHARE_SPLINE: 'paid-traffic-share-spline',
  /** Column chart for top entity paid traffic with double dropdown */
  PAID_TRAFFIC_TOP_ENTITY_COLUMN: 'paid-traffic-top-entity-column',
  /** Spline chart for products with paid search traffic over time */
  PAID_TRAFFIC_PRODUCTS_SPLINE: 'paid-traffic-products-spline',
  /** Spline chart for keyword count with paid search traffic over time */
  PAID_TRAFFIC_KEYWORD_COUNT_SPLINE: 'paid-traffic-keyword-count-spline',

  // ── Other Traffic charts ──

  /** Spline chart for other traffic over time */
  OTHER_TRAFFIC_SPLINE: 'other-traffic-spline',
  /** Spline chart for products with other traffic over time */
  OTHER_TRAFFIC_PRODUCTS_SPLINE: 'other-traffic-products-spline',

  // ── Sales Summary charts ──

  /** Spline chart for retail sales (1P/3P selector or plain) */
  RETAIL_SALES_SPLINE: 'retail-sales-spline',
  /** Spline chart for market share of retail sales */
  MARKET_SHARE_RETAIL_SALES_SPLINE: 'market-share-retail-sales-spline',
  /** Spline chart for product count */
  SALES_PRODUCT_COUNT_SPLINE: 'sales-product-count-spline',
  /** Spline chart for 1P retail sales as % of total */
  SALES_1P_PERCENT_SPLINE: 'sales-1p-percent-spline',
  /** Column chart for market share of retail sales by entity */
  SALES_MARKET_SHARE_COLUMN_CHART: 'sales-market-share-column-chart',
  /** Column chart for top entity retail sales with double dropdown */
  SALES_TOP_ENTITY_COLUMN: 'sales-top-entity-column',
  /** Spline chart for product market share of retail sales */
  SALES_MARKET_SHARE_PRODUCT_SPLINE: 'sales-market-share-product-spline',

  // ── Conversion charts ──

  /** Spline chart for conversion rate over time */
  CONVERSION_SPLINE_CHART: 'conversion-spline-chart',
  /** Column chart for conversion rate by top entity */
  CONVERSION_TOP_ENTITY_COLUMN: 'conversion-top-entity-column',

  // ── Price Summary charts ──

  /** Spline chart for retail price over time */
  PRICE_RETAIL_PRICE_SPLINE: 'price-retail-price-spline',
  /** Spline chart for price index over time */
  PRICE_INDEX_SPLINE: 'price-index-spline',
  /** Column chart for price by top entity */
  PRICE_TOP_ENTITY_COLUMN: 'price-top-entity-column',

  // ── Price Promotion charts ──

  /** Spline chart for promotions overview */
  PROMOTION_TOP_SECTION_SPLINE: 'promotion-top-section-spline',
  /** Column chart for promotions by top entity */
  PROMOTION_TOP_ENTITY_COLUMN: 'promotion-top-entity-column',
  /** Column chart for promotions by deal type */
  PROMOTION_DEAL_TYPE_COLUMN: 'promotion-deal-type-column',

  // ── Units Sold charts ──

  /** Spline chart for units sold over time */
  UNITS_SOLD_SPLINE_CHART: 'units-sold-spline-chart',
  /** Spline chart for market share of units sold */
  UNITS_SOLD_MARKET_SHARE_SPLINE: 'units-sold-market-share-spline',
  /** Spline chart for 1P total units sold */
  UNITS_SOLD_1P_TOTAL_SPLINE: 'units-sold-1p-total-spline',
  /** Column chart for market share of units sold by entity */
  UNITS_SOLD_MARKET_SHARE_COLUMN_CHART: 'units-sold-market-share-column-chart',
  /** Column chart for top entity units sold */
  UNITS_SOLD_TOP_ENTITY_COLUMN: 'units-sold-top-entity-column',
  /** Spline chart for product market share of units sold */
  UNITS_SOLD_MARKET_SHARE_PRODUCT_SPLINE: 'units-sold-market-share-product-spline',
  /** Spline chart for non-1P market share of units sold */
  UNITS_SOLD_NON1P_MARKET_SHARE_SPLINE: 'units-sold-non1p-market-share-spline',
  /** Column chart for non-1P market share of units sold by entity */
  UNITS_SOLD_NON1P_MARKET_SHARE_COLUMN_CHART: 'units-sold-non1p-market-share-column-chart',
  /** Column chart for non-1P top entity units sold */
  UNITS_SOLD_NON1P_TOP_ENTITY_COLUMN: 'units-sold-non1p-top-entity-column'
} as const;

export type ChartName = (typeof CHART_NAMES)[keyof typeof CHART_NAMES];
